import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { LenderUserState } from 'ng2-app/store/lender-user/lender-user/lender-user.state';
import { LenderUser } from 'ng2-app/models/dto/lender-user.model';
import { UserActionItemsState } from 'ng2-app/modules/today/state/user-action-items.state';
import { combineLatest, filter, map, Observable, Subject, take, takeUntil } from 'rxjs';
import { UserThreadsState } from 'ng2-app/modules/today/state/email/user-threads.state';
import { Thread } from 'ng2-app/models/message/message.model';
import {
  AddBorrowerToThread,
  GetThread,
  GetUserThreads,
  MarkThreadUnread,
  UnsetActiveUserThread
} from 'ng2-app/modules/today/state/email/user-threads.action';
import { BorrowerState } from 'ng2-app/store/borrower/borrower/borrower.state';
import { Borrower } from 'app/models/dto/borrower.model';
import { BorrowerValueState } from 'ng2-app/store/borrower-value/borrower-value.state';
import { Message } from 'ng2-app/messages/message-service/message.model';
import { MarkThreadRead, SendMessage, ThreadReply } from 'ng2-app/messages/message-store/message.actions';
import { ClearBorrowerStore } from 'ng2-app/store/borrower/borrower/borrower.actions';
import { LinkMessageToBorrowerDialogComponent } from 'ng2-app/modules/task-management/components/unknown-inbox-items/link-message-to-borrower-dialog/link-message-to-borrower-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { AddNewLeadFormComponent } from 'ng2-app/add-new-lead/add-new-lead-form/add-new-lead-form.component';
import { TodayBadgeCountsState } from 'ng2-app/modules/today/state/today-badge-counts/today-badge-counts.state';
import { MatMenuTrigger } from '@angular/material/menu';
import { Contact } from 'app/models/dto/contact.model';
import { ContactState } from 'app/store/contact/contact.state';
import { NonOwnerPersonnelState } from 'app/store/non-owner-personnel/non-owner-personnel.state';
import { GetContactsForBorrower } from 'app/store/contact/contact.actions';
import { GetNonOwnerPersonnelForBorrower } from 'app/store/non-owner-personnel/non-owner-personnel.actions';

interface JoinedContacts extends Contact {
  disabled: boolean;
}

@Component({
  selector: 'app-user-email-list-view',
  templateUrl: './user-email-list-view.component.html',
  styleUrls: ['./user-email-list-view.component.scss']
})
export class UserEmailListViewComponent implements OnInit, OnDestroy {

  @Select(LenderUserState.lenderUser) lenderUser$: Observable<LenderUser>;
  @Select(UserActionItemsState.user) user$: Observable<any>;
  @Select(UserThreadsState.threads) threads$: Observable<Thread[]>;
  @Select(UserThreadsState.activeThread) activeThread$: Observable<Thread>;
  @Select(UserThreadsState.listLoading) listLoading$: Observable<boolean>;
  @Select(UserThreadsState.threadLoading) threadLoading$: Observable<boolean>;
  @Select(UserThreadsState.total) threadsTotal$: Observable<number>;
  @Select(UserThreadsState.showLoadMore) showLoadMore$: Observable<boolean>;
  @Select(BorrowerState.borrower) borrower$: Observable<Borrower>;
  @Select(BorrowerState.loading) borrowerLoading$: Observable<boolean>;
  @Select(TodayBadgeCountsState.unknownUnreadEmailThreads) unknownUnreadEmailThreads$: Observable<number>;
  @Select(ContactState.contacts) contacts$: Observable<JoinedContacts[]>;
  @Select(NonOwnerPersonnelState.nonOwnerPersonnel) nonOwnerPersonnel$: Observable<JoinedContacts[]>;
  @ViewChild(MatMenuTrigger) contextMenu: MatMenuTrigger;

  borrowerOrThreadLoading$: Observable<boolean>;
  complianceReview$: Observable<any>;
  destroyed$ = new Subject<boolean>();
  validStatus$: Observable<boolean>;
  showLoadMoreButton$: Observable<boolean>;
  contextMenuPosition = { x: '0px', y: '0px' };

  // is the currently logged-in user the selected user (UserActionItemsState.user)
  actionItemsUserIsAuthUser: boolean = true;

  page: number = 1;
  selectedType: string = 'unread';
  sortDirection: string = 'desc';
  jsonParse = JSON.parse;
  showEditor: boolean = false;

  joinedContacts$: Observable<JoinedContacts[]>;

  constructor(
    private store: Store,
    public dialog: MatDialog,
  ) { }

  ngOnInit(): void {
    this.store.dispatch( new ClearBorrowerStore() );

    combineLatest([this.user$, this.lenderUser$])
      .pipe(
        takeUntil(this.destroyed$),
        map(([user, lenderUser]) => {
          return user.id === lenderUser.id;
        })
      ).subscribe( result => {
      this.actionItemsUserIsAuthUser = result;
      this.resetAndFetch();
      this.loadContactForBorrower();
    });

    this.complianceReview$ = this.store.select(BorrowerValueState.valueByAlias)
      .pipe(
        takeUntil(this.destroyed$),
        map(filterFn => filterFn('complianceReview')
        ));

    this.validStatus$ = combineLatest([this.complianceReview$, this.borrower$])
      .pipe(
        filter(([complianceReview, borrower]) => !!complianceReview && !!borrower),
        takeUntil(this.destroyed$),
        map(([complianceReview, borrower]) => {
          return borrower !== null &&
            !['doNotContact', 'dead'].includes(borrower?.status) &&
            complianceReview?.value === null;
        })
      );

    this.borrowerOrThreadLoading$ = combineLatest([this.borrowerLoading$, this.threadLoading$])
      .pipe(
        takeUntil(this.destroyed$),
        map(([borrowerLoading, threadLoading]) => {
          return borrowerLoading || threadLoading;
        })
      );

    this.showLoadMoreButton$ =
      combineLatest([
        this.borrowerOrThreadLoading$,
        this.showLoadMore$,
      ]).pipe(
        takeUntil(this.destroyed$),
        map(([loading, showLoadMore]) => {
          return !loading && showLoadMore;
        })
      );
  }

  loadContactForBorrower(): void {
    this.joinTheContacts();
    this.borrower$.pipe(takeUntil(this.destroyed$)).subscribe(borrower => {
      if (!!borrower) {
        this.store.dispatch(new GetContactsForBorrower(borrower.id));
        this.store.dispatch(new GetNonOwnerPersonnelForBorrower(borrower.id));
      }
    });
  }

  joinTheContacts(): void {
    this.joinedContacts$ = combineLatest([this.contacts$, this.nonOwnerPersonnel$]).pipe(
      map(([contacts, nonOwnerPersonnel]) => {
        const nopes: JoinedContacts[] = [];
        nonOwnerPersonnel.forEach(nop => {
          if (!nop.email) {
            nop.disabled = true;
            nopes.push(nop);
          } else {
            nopes.push(nop);
          }
        });
        return contacts.concat(nopes);
      })
    );
  }

  fetchThread(threadId: number): void {
    this.activeThread$.pipe(take(1))
      .subscribe( activeThread => {
        if(activeThread && activeThread.id && activeThread.id !== threadId) {
          this.store.dispatch( new GetThread( threadId, false ) );
          this.markThreadRead(threadId);
        }
      });
    }
    
  markThreadRead(threadId: number): void {
    this.store.dispatch(new MarkThreadRead(threadId));
  }

  getThreads(reset: boolean): void {
    this.user$.pipe(
      take(1)
    ).subscribe( ({ id }) => {
      this.store.dispatch(new GetUserThreads(id, reset, this.page, this.selectedType, this.sortDirection));
    });

  }

  resetAndFetch(): void {
    // changing type filters or sorting should reset the total fetch count / pagination
    this.page = 1;
    this.store.dispatch( new UnsetActiveUserThread());
    this.getThreads(true);
  }

  loadMore(): void {
    const unread = this.selectedType === 'unread';
    this.page = unread ? 1 : ++this.page;
    this.getThreads(unread);
  }

  handleEmailCreate(message: Message): void {

    message.threadId ?
      this.store.dispatch(new ThreadReply(message.threadId, message)) :
      this.store.dispatch(new SendMessage(message));
  }

  linkThreadToBorrower(thread: Thread) {
    const dialogRef = this.dialog.open(LinkMessageToBorrowerDialogComponent, {
      data: { message: thread?.messages[0] }
    });
    dialogRef.afterClosed().pipe(take(1)).subscribe(data => {
      if (data) {
        this.store.dispatch(new AddBorrowerToThread(data.borrower, thread.id));
      }
    });
  }

  linkThreadToNewBorrower(thread: Thread) {
    const dialogRef = this.dialog.open(AddNewLeadFormComponent, {
      width: '28rem',
      maxHeight: '100vh',
      panelClass: 'addNewLeadDialogContainer',
      data: {
        navigateToNewBorrower: false
      },
    });

    dialogRef.afterClosed().pipe(take(1)).subscribe(data => {
      if (data) {
        this.store.dispatch(new AddBorrowerToThread(data.borrower, thread.id));
      }
    });
  }

  toggleMessageEditor(show: boolean) {
    this.showEditor = show;
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
    this.store.dispatch( new UnsetActiveUserThread() );
  }

  onContextMenu($event, thread) {
    $event.preventDefault();
    this.contextMenuPosition.x = $event.clientX + 'px';
    this.contextMenuPosition.y = $event.clientY + 'px';
    this.contextMenu.menuData = { thread };
    this.contextMenu.menu?.focusFirstItem('mouse');
    this.contextMenu.openMenu();
  }

  contextMenuMarkUnread() {
    const thread = this.contextMenu.menuData.thread;
    if (thread) {
      this.store.dispatch( new MarkThreadUnread(thread.id) );
    }
  }
}
